import { Space, Input, Select, DatePicker, Button } from "antd";
import { SearchOutlined, ClearOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useSelect } from "@refinedev/antd";
import type { IncidenceFilters as IIncidenceFilters } from "../../../types";
import dayjs, { Dayjs } from "dayjs";

const { RangePicker } = DatePicker;

interface IncidenceFiltersProps {
  filters: IIncidenceFilters;
  onFilterChange: (filters: IIncidenceFilters) => void;
  onClearFilters: () => void;
}

/**
 * IncidenceFilters Component
 * Provides filtering controls for the incidence list
 */
export const IncidenceFilters: React.FC<IncidenceFiltersProps> = ({
  filters,
  onFilterChange,
  onClearFilters,
}) => {
  const { t } = useTranslation();

  // Fetch incidence types for the filter dropdown. After the
  // Incidence → Ticket unification these live as `TicketCategory`
  // rows with kind="audit".
  const { selectProps: kindIncidenceSelectProps } = useSelect({
    resource: "v1/ticket-categories",
    optionLabel: "name",
    optionValue: "id",
    pagination: { pageSize: 200 },
    filters: [
      { field: "kind", operator: "eq", value: "audit" },
    ],
  });

  // Fetch users for assigned_to filter dropdown
  const { selectProps: userSelectProps } = useSelect({
    resource: "app-users",
    optionLabel: "name",
    optionValue: "id",
  });

  // Status options
  const statusOptions = [
    { label: t("incidence.status.open", "Abierta"), value: 1 },
    { label: t("incidence.status.closed", "Cerrada"), value: 2 },
    { label: t("incidence.status.cancelled", "Cancelada"), value: 3 },
  ];

  // Priority options
  const priorityOptions = [
    { label: t("incidence.priority.low", "Baja"), value: "low" },
    { label: t("incidence.priority.medium", "Media"), value: "medium" },
    { label: t("incidence.priority.high", "Alta"), value: "high" },
    { label: t("incidence.priority.critical", "Crítica"), value: "critical" },
  ];

  // Handle search input change
  const handleSearchChange = (value: string) => {
    onFilterChange({ ...filters, search: value || undefined });
  };

  // Handle status filter change
  const handleStatusChange = (value: number | undefined) => {
    onFilterChange({ ...filters, status_incidence: value as any });
  };

  // Handle priority filter change
  const handlePriorityChange = (value: string | undefined) => {
    onFilterChange({ ...filters, priority: value as any });
  };

  // Handle kind incidence filter change
  const handleKindIncidenceChange = (value: number | undefined) => {
    onFilterChange({ ...filters, kind_incidence: value });
  };

  // Handle assigned to filter change
  const handleAssignedToChange = (value: number | undefined) => {
    onFilterChange({ ...filters, assigned_to: value });
  };

  // Handle date range change
  const handleDateRangeChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    if (dates && dates[0] && dates[1]) {
      onFilterChange({
        ...filters,
        deadline_from: dates[0].format("YYYY-MM-DD"),
        deadline_to: dates[1].format("YYYY-MM-DD"),
      });
    } else {
      const { deadline_from, deadline_to, ...rest } = filters;
      onFilterChange(rest);
    }
  };

  // Check if any filters are active
  const hasActiveFilters =
    filters.search ||
    filters.status_incidence !== undefined ||
    filters.priority !== undefined ||
    filters.kind_incidence !== undefined ||
    filters.assigned_to !== undefined ||
    filters.deadline_from ||
    filters.deadline_to;

  // Get date range value for RangePicker
  const dateRangeValue: [Dayjs, Dayjs] | null =
    filters.deadline_from && filters.deadline_to
      ? [dayjs(filters.deadline_from), dayjs(filters.deadline_to)]
      : null;

  return (
    <Space
      direction="vertical"
      size="middle"
      style={{ width: "100%", marginBottom: 16 }}
    >
      <Space wrap style={{ justifyContent: "space-between", width: "100%" }}>
        <Space wrap>
          {/* Search Input */}
          <Input.Search
            placeholder={t(
              "incidence.search",
              "Buscar por número o descripción..."
            )}
            allowClear
            enterButton={<SearchOutlined />}
            size="middle"
            style={{ width: 350 }}
            value={filters.search || ""}
            onChange={(e) => handleSearchChange(e.target.value)}
            onSearch={handleSearchChange}
          />

          {/* Status Filter */}
          <Select
            options={statusOptions}
            placeholder={t("incidence.filter.status", "Estado")}
            style={{ width: 150 }}
            allowClear
            value={filters.status_incidence}
            onChange={handleStatusChange}
          />

          {/* Priority Filter */}
          <Select
            options={priorityOptions}
            placeholder={t("incidence.filter.priority", "Prioridad")}
            style={{ width: 150 }}
            allowClear
            value={filters.priority}
            onChange={handlePriorityChange}
          />

          {/* Kind Incidence Filter */}
          <Select
            {...kindIncidenceSelectProps}
            placeholder={t("incidence.filter.type", "Tipo de incidencia")}
            style={{ width: 200 }}
            allowClear
            value={filters.kind_incidence}
            onChange={handleKindIncidenceChange}
          />

          {/* Assigned To Filter */}
          <Select
            {...userSelectProps}
            placeholder={t("incidence.filter.assigned_to", "Asignado a")}
            style={{ width: 180 }}
            allowClear
            showSearch
            value={filters.assigned_to}
            onChange={handleAssignedToChange}
          />

          {/* Date Range Filter */}
          <RangePicker
            placeholder={[
              t("incidence.filter.deadline_from", "Fecha desde"),
              t("incidence.filter.deadline_to", "Fecha hasta"),
            ]}
            style={{ width: 280 }}
            format="DD/MM/YYYY"
            value={dateRangeValue}
            onChange={handleDateRangeChange}
          />
        </Space>

        {/* Clear Filters Button */}
        {hasActiveFilters && (
          <Button
            icon={<ClearOutlined />}
            onClick={onClearFilters}
            type="default"
          >
            {t("common.clearFilters", "Limpiar filtros")}
          </Button>
        )}
      </Space>
    </Space>
  );
};
